/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Link } from '@/components/link';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/typography';
import { useTranslation } from 'react-i18next';
import {
    OrderListItem,
    type OrderListItemData,
    type OrderProductItem,
    type PickupLocation,
} from '@/components/account/order-list-item';
import type { OrderReturnStatusType, OrderStatusType } from '@/lib/order/status';
import { routes } from '@/route-paths';

/** Re-export for consumers. Single source of truth: @/lib/order/status */
export type { OrderStatusType };

/**
 * Order data structure for display.
 * Extended to support the new OrderListItem component.
 */
export type Order = {
    orderNo: string;
    orderDate: string;
    status: string;
    statusLabel?: string;
    /** Derived order-level cancel status; takes priority over return and raw status. */
    cancelStatus?: 'cancelled';
    /** Derived order-level return status; overrides the raw status badge when set. */
    returnStatus?: OrderReturnStatusType;
    total: number;
    currency?: string;
    itemCount: number;
    productItems?: OrderProductItem[];
    pickupLocation?: PickupLocation;
};

/**
 * Props for the OrderList component.
 */
export type OrderListProps = {
    title: string;
    subtitle?: string;
    orders: Order[];
    emptyMessage?: string;
    /** Maximum number of product thumbnails per order */
    maxThumbnails?: number;
    /** Callback when View Details is clicked */
    onViewDetails?: (orderNo: string) => void;
};

/**
 * Convert Order to OrderListItemData format.
 */
function toOrderListItemData(order: Order): OrderListItemData {
    return {
        orderNo: order.orderNo,
        orderDate: order.orderDate,
        total: order.total,
        currency: order.currency,
        status: order.status,
        statusLabel: order.statusLabel,
        cancelStatus: order.cancelStatus,
        returnStatus: order.returnStatus,
        itemCount: order.itemCount,
        productItems: order.productItems,
        pickupLocation: order.pickupLocation,
    };
}

/**
 * Empty state displayed when the customer has no orders.
 */
function OrderListEmpty({ message }: { message?: string }): ReactElement {
    const { t } = useTranslation('account');

    // RGBARRY-CUSTOMIZATION: give the empty-order message balanced spacing and body-sized typography.
    return (
        <Card className="border-muted-foreground/10 m-0 border-b-0 py-0">
            <CardContent className="px-6 py-8 sm:py-10">
                <div className="flex min-h-32 flex-col items-center justify-center gap-4 text-center">
                    <Typography variant="muted" as="p" align="center" className="max-w-md px-4 leading-6 sm:px-0">
                        {message || t('orders.empty')}
                    </Typography>
                    <Button asChild>
                        <Link to={routes.home}>{t('orders.continueShopping')}</Link>
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * Page header for the order list with title and optional subtitle.
 * Extracted so the route can render it outside Suspense for instant display.
 */
export function OrderListHeader({ title, subtitle }: { title: string; subtitle?: string }): ReactElement {
    return (
        <Card className="bg-card border-muted-foreground/10 rounded-b-none border-b-0">
            <CardContent className="px-5">
                <Typography variant="h4" className="text-foreground mb-1.5">
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="small" as="p" className="text-sm text-muted-foreground">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Order items body with footer. Renders order cards, empty state, and a total-count line.
 * Designed to be used inside Suspense/Await so the header can render instantly.
 *
 * RGBARRY-CUSTOMIZATION (order history: "View more" replaces numbered pagination): the offset/limit
 * prev-next pagination was removed in favour of a forward-cursor "View more" control (rendered by the
 * caller, e.g. OrderHistoryView, below this body). This component now only reports the count. When
 * `total` is a number greater than what's loaded, it shows "Viewing X of Y orders"; otherwise it shows
 * "Viewing N orders". `countId` + `aria-live` let the caller's button describe itself and have new
 * counts announced to assistive tech as windows stream in.
 */
export function OrderListBody({
    orders,
    emptyMessage,
    maxThumbnails = 12,
    onViewDetails,
    total: totalCount,
    loadedCount,
    countId,
}: Omit<OrderListProps, 'title' | 'subtitle'> & {
    /** Visible order count for the "X of Y" label, or null/undefined when unknown (shows plain count). */
    total?: number | null;
    /** How many orders are currently rendered; defaults to `orders.length`. */
    loadedCount?: number;
    /** id for the count line, so a "View more" button can reference it via aria-describedby. */
    countId?: string;
}): ReactElement {
    const { t } = useTranslation('account');

    const shownCount = typeof loadedCount === 'number' ? loadedCount : orders.length;
    // Show "X of Y" only when a known total still exceeds what's loaded; otherwise a plain count reads
    // better ("Viewing 5 orders" rather than "Viewing 5 of 5 orders").
    const showOfTotal = typeof totalCount === 'number' && totalCount > shownCount;

    return (
        <>
            {orders.length === 0 ? (
                <OrderListEmpty message={emptyMessage} />
            ) : (
                <ul role="list" className="space-y-4 m-0 border-x border-t border-muted-foreground/10 rounded-t-none">
                    {orders.map((order) => (
                        <li key={order.orderNo}>
                            <OrderListItem
                                order={toOrderListItemData(order)}
                                maxThumbnails={maxThumbnails}
                                onViewDetails={onViewDetails}
                            />
                        </li>
                    ))}
                </ul>
            )}
            <div className="p-6 m-0 border-b border-x border-muted-foreground/10 flex flex-row items-center w-full gap-4">
                <Typography
                    variant="small"
                    as="p"
                    id={countId}
                    aria-live="polite"
                    className="text-muted-foreground min-w-0 shrink"
                    data-testid="total-orders-text">
                    {showOfTotal
                        ? t('orders.totalOrdersLoaded', { loaded: shownCount, count: totalCount })
                        : t('orders.totalOrders', { count: shownCount })}
                </Typography>
            </div>
        </>
    );
}

/**
 * Reusable order list component that displays a list of order cards.
 * Composes OrderListHeader + OrderListBody for convenience.
 *
 * @example
 * ```tsx
 * <OrderList
 *   title="Order History"
 *   subtitle="View and track your orders"
 *   orders={orders}
 *   onViewDetails={(orderNo) => navigate(`/orders/${orderNo}`)}
 * />
 * ```
 */
export function OrderList({
    title,
    subtitle,
    orders,
    emptyMessage,
    maxThumbnails = 12,
    onViewDetails,
}: OrderListProps): ReactElement {
    return (
        <div className="space-y-5">
            <OrderListHeader title={title} subtitle={subtitle} />
            <OrderListBody
                orders={orders}
                emptyMessage={emptyMessage}
                maxThumbnails={maxThumbnails}
                onViewDetails={onViewDetails}
            />
        </div>
    );
}

/**
 * Skeleton for order list items. Renders a configurable number of placeholder
 * cards that match the real OrderListItem layout, plus a footer bar.
 * Used as the Suspense fallback on both the order-history page and the
 * account-overview recent-orders section.
 */
export function OrderListSkeleton(): ReactElement {
    return (
        <>
            <div className="space-y-4 m-0 border-x border-t border-muted-foreground/10">
                {Array.from({ length: 3 }, (_, i) => (
                    <Card key={i} className="py-0 border-0 border-muted-foreground/10">
                        <CardContent className="p-6 space-y-4 border-b border-muted-foreground/10 animate-pulse">
                            <div className="flex flex-wrap items-start justify-between border-b border-muted-foreground/10 -mx-6 -mt-6 px-6 pt-3 pb-3 mb-6 bg-muted">
                                <div className="flex flex-wrap gap-x-8 gap-y-2">
                                    <div className="h-10 w-24 bg-muted-foreground/20 rounded-ui" />
                                    <div className="h-10 w-20 bg-muted-foreground/20 rounded-ui" />
                                    <div className="h-10 w-16 bg-muted-foreground/20 rounded-ui" />
                                </div>
                                <div className="h-8 w-24 bg-muted-foreground/20 rounded-full" />
                            </div>
                            <div className="flex gap-2">
                                <div className="w-16 h-16 bg-muted-foreground/20 rounded-ui" />
                                <div className="w-16 h-16 bg-muted-foreground/20 rounded-ui" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
            <div className="p-6 m-0 border-b border-x border-muted-foreground/10">
                <div className="h-5 w-32 bg-muted-foreground/20 rounded-ui" />
            </div>
        </>
    );
}

// Re-export types from OrderListItem for convenience
export type { OrderProductItem, PickupLocation, OrderListItemData };

export default OrderList;
